import React, { useState } from "react";
import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  updateProfile,
} from "firebase/auth";
import { doc, setDoc, getDoc } from "firebase/firestore";
import { auth, db } from "../firebaseConfig";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Apple, Lock, User } from "lucide-react";
import ro from "/src/content/ro.json";

const Register = () => {
  const [form, setForm] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    name: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  // Email + parola
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    if (form.password !== form.confirmPassword) {
      setError(ro.auth.register.errorMismatch);
      setLoading(false);
      return;
    }

    try {
      const userCred = await createUserWithEmailAndPassword(
        auth,
        form.email,
        form.password
      );
      const user = userCred.user;

      await sendEmailVerification(user);

      await setDoc(doc(db, "users", user.uid), {
        uid: user.uid,
        name: form.name || "Utilizator nou",
        email: user.email,
        createdAt: new Date(),
        provider: "email",
      });

      alert(ro.auth.register.successAlert);
      navigate("/login");
    } catch (err) {
      console.error(err);
      setError(ro.auth.register.errorCreate);
    } finally {
      setLoading(false);
    }
  };

  // Register/Login cu Google
  const handleGoogleRegister = async () => {
    const provider = new GoogleAuthProvider();
    provider.addScope("profile");
    provider.addScope("email");
    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      const providerData = user.providerData?.[0] || {};
      const tokenResponse = result?._tokenResponse || {};
      const providerPhoto =
        user.photoURL ||
        providerData.photoURL ||
        tokenResponse.photoUrl ||
        tokenResponse.photoURL ||
        null;
      const providerName =
        user.displayName ||
        providerData.displayName ||
        tokenResponse.displayName ||
        null;
      if (providerPhoto || providerName) {
        await updateProfile(user, {
          photoURL: providerPhoto || undefined,
          displayName: providerName || undefined,
        });
        await user.reload();
      }

      const userRef = doc(db, "users", user.uid);
      const snap = await getDoc(userRef);
      const payload = {
        uid: user.uid,
        name: user.displayName,
        email: user.email,
        photoURL: providerPhoto || user.photoURL || null,
        provider: "google",
      };
      if (!snap.exists()) payload.createdAt = new Date();
      await setDoc(userRef, payload, { merge: true });

      navigate("/");
    } catch (err) {
      console.error(err);
      setError(ro.auth.register.errorGoogle);
    }
  };

  // Register/Login cu Apple
  const handleAppleRegister = async () => {
    const provider = new OAuthProvider("apple.com");
    provider.addScope("email");
    provider.addScope("name");
    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      const providerData = user.providerData?.[0] || {};
      const providerPhoto = user.photoURL || providerData.photoURL || null;
      const providerName = user.displayName || providerData.displayName || null;
      if (providerPhoto || providerName) {
        await updateProfile(user, {
          photoURL: providerPhoto || undefined,
          displayName: providerName || undefined,
        });
        await user.reload();
      }

      const userRef = doc(db, "users", user.uid);
      const snap = await getDoc(userRef);
      const payload = {
        uid: user.uid,
        name: user.displayName || "Apple User",
        email: user.email,
        photoURL: providerPhoto || user.photoURL || null,
        provider: "apple",
      };
      if (!snap.exists()) payload.createdAt = new Date();
      await setDoc(userRef, payload, { merge: true });

      navigate("/");
    } catch (err) {
      console.error(err);
      setError(ro.auth.register.errorApple);
    }
  };

  return (
    <section className="auth-section">
      <div className="auth-card">
        <h2 className="auth-title">{ro.auth.register.title}</h2>

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="auth-input-wrap">
            <input
              type="text"
              name="name"
              placeholder={ro.auth.register.namePlaceholder}
              value={form.name}
              onChange={handleChange}
              className="auth-input"
              autoComplete="name"
              required
            />
            <span className="auth-input-icon">
              <User size={18} />
            </span>
          </div>
          <div className="auth-input-wrap">
            <input
              type="email"
              name="email"
              placeholder={ro.auth.register.emailPlaceholder}
              value={form.email}
              onChange={handleChange}
              className="auth-input"
              autoComplete="email"
              required
            />
            <span className="auth-input-icon">
              <Mail size={18} />
            </span>
          </div>
          <div className="auth-input-wrap">
            <input
              type="password"
              name="password"
              placeholder={ro.auth.register.passwordPlaceholder}
              value={form.password}
              onChange={handleChange}
              className="auth-input"
              autoComplete="new-password"
              required
            />
            <span className="auth-input-icon">
              <Lock size={18} />
            </span>
          </div>
          <div className="auth-input-wrap">
            <input
              type="password"
              name="confirmPassword"
              placeholder={ro.auth.register.confirmPlaceholder}
              value={form.confirmPassword}
              onChange={handleChange}
              className="auth-input"
              autoComplete="new-password"
              required
            />
            <span className="auth-input-icon">
              <Lock size={18} />
            </span>
          </div>
          <button type="submit" className="auth-btn" disabled={loading}>
            {loading ? ro.auth.register.submitLoading : ro.auth.register.submit}
          </button>
        </form>

        {error && <p className="auth-message text-red-600">{error}</p>}

        <div className="mt-4 flex flex-col gap-3">
          <button onClick={handleGoogleRegister} className="auth-social google">
            <span className="auth-social-icon google" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="18" height="18">
                <path
                  fill="#EA4335"
                  d="M12 10.2v3.9h5.4c-.2 1.2-1.4 3.5-5.4 3.5-3.2 0-5.9-2.6-5.9-5.9S8.8 6 12 6c1.8 0 3 0.8 3.7 1.4l2.5-2.4C16.9 3.7 14.7 2.8 12 2.8 7 2.8 2.9 6.9 2.9 12S7 21.2 12 21.2c5.6 0 9.3-3.9 9.3-9.4 0-.6-.1-1.1-.2-1.6H12z"
                />
                <path
                  fill="#FBBC05"
                  d="M3.9 7.4l3.2 2.3c.9-1.7 2.6-2.8 4.9-2.8 1.8 0 3 0.8 3.7 1.4l2.5-2.4C16.9 3.7 14.7 2.8 12 2.8c-3.5 0-6.6 2-8.1 4.6z"
                />
                <path
                  fill="#34A853"
                  d="M12 21.2c2.7 0 5-0.9 6.7-2.5l-3.1-2.4c-.8.6-1.9 1-3.6 1-2.9 0-5.3-2-6.2-4.8l-3.2 2.4c1.5 3 4.6 5.3 9.4 5.3z"
                />
                <path
                  fill="#4285F4"
                  d="M21.3 11.8c0-.6-.1-1.1-.2-1.6H12v3.9h5.4c-.3 1.5-1.7 2.9-3.8 3.7l3.1 2.4c1.8-1.7 2.6-4.1 2.6-6.9z"
                />
              </svg>
            </span>
            {ro.auth.register.google}
          </button>

          <button onClick={handleAppleRegister} className="auth-social apple">
            <Apple size={18} /> {ro.auth.register.apple}
          </button>
        </div>

        <div className="auth-links">
          <Link to="/login" className="auth-link">
            {ro.auth.register.haveAccount}
          </Link>
        </div>
      </div>
    </section>
  );
};

export default Register;
export { Register };
